import { useCallback, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import useGeneralHook from 'common/hook/useGeneralHook';
import useService from 'common/hook/useService';
import { handleException } from 'modules/account/detail/helpers/utils';
import useGetListClients from 'modules/account/services/useGetListClients';
import useCreateAccount, {
  defaultAccountInfo,
  defaultRolesAssignment,
  defaultRolesToAssign,
  defaultUserInfo,
} from 'modules/account/services/useCreateAccount';
import type {
  AccountInfo,
  CurrentSelectedRole,
  ReplaceCheckUserExists,
  RolesToAssign,
  Unit,
  UserInfo,
} from 'modules/account/types';
import { buildCreateUserPayload, validateCreatePayload } from 'modules/account/create/utils';
import type { StepIndex } from 'modules/account/create/steps/stepsConfig';

// --- State ---

export interface AccountCreateFormState {
  activeStep: StepIndex;
  accountInfo: AccountInfo;
  unitAddress: Unit | undefined;
  userInfo: UserInfo;
  currentSelected: CurrentSelectedRole | undefined;
  rolesToAssign: RolesToAssign;
}

const initialState: AccountCreateFormState = {
  activeStep: 0,
  accountInfo: { ...defaultAccountInfo },
  unitAddress: undefined,
  userInfo: { ...defaultUserInfo },
  currentSelected: undefined,
  rolesToAssign: { ...defaultRolesToAssign },
};

// --- Hook ---

export function useAccountCreateForm() {
  const { formatMessage } = useIntl();
  const { enqueueSnackbar } = useGeneralHook();
  const service = useService();
  const { data: clients } = useGetListClients();

  const [state, setState] = useState<AccountCreateFormState>(initialState);
  const stateRef = useRef(state);
  stateRef.current = state;
  const isCQQL = state.userInfo.organizationTypeId === 'CQQL';

  // Reset về trạng thái ban đầu
  const resetAllData = useCallback(() => {
    setState({
      ...initialState,
      rolesToAssign: {
        ...initialState.rolesToAssign,
        rolesAssignment: { ...defaultRolesAssignment },
      },
    });
  }, []);

  const { mutateAsync: createAccount, isPending: isCreatePending } = useCreateAccount({
    onSuccess: resetAllData,
  });

  // --- Navigation ---

  const goNextStep = useCallback(() => {
    setState((s) => ({ ...s, activeStep: (s.activeStep + 1) as StepIndex }));
  }, []);

  const goPreviousStep = useCallback(() => {
    setState((s) => ({
      ...s,
      activeStep: (s.activeStep - 1) as StepIndex,
    }));
  }, []);

  const setActiveStep = useCallback((step: StepIndex) => {
    setState((s) => ({ ...s, activeStep: step }));
  }, []);

  // --- Step 1 handlers ---

  const handleSetAccountInfo = useCallback(
    async (formValue: AccountInfo) => {
      try {
        setState((s) => ({ ...s, accountInfo: formValue }));

        if (!formValue.username && !formValue.email) return;
        if (!service) return;

        const result: ReplaceCheckUserExists = await service.UserManagementController_getUserId({
          params: { handle: formValue.username || formValue.email },
        });

        if (result.userId === null) {
          goNextStep();
        } else {
          enqueueSnackbar({
            message: formatMessage({ id: 'account.error.alreadyExists' }),
            variant: 'error',
          });
        }
      } catch (error) {
        const axiosError = handleException(error, { notifyUser: false });
        if (axiosError === 403) {
          enqueueSnackbar({
            message: formatMessage({ id: 'account.error.notAllowedToCreate' }),
            variant: 'error',
          });
        } else if (axiosError) {
          enqueueSnackbar({
            message: formatMessage({ id: 'error.error' }),
            variant: 'error',
          });
        }
      }
    },
    [formatMessage, enqueueSnackbar, service, goNextStep]
  );

  // --- Step 2 handlers ---

  const handleSetUserInfo = useCallback((formValue: UserInfo) => {
    setState((s) => ({ ...s, userInfo: formValue }));
  }, []);

  const handleBackToStepOne = useCallback(
    (formValue: UserInfo) => {
      handleSetUserInfo(formValue);
      setActiveStep(0);
    },
    [handleSetUserInfo, setActiveStep]
  );

  const handleSetUnitAddress = useCallback((unit: Unit) => {
    setState((s) => ({
      ...s,
      unitAddress: unit,
      rolesToAssign: {
        ...s.rolesToAssign,
        rolesAssignment: { ...defaultRolesAssignment },
      },
    }));
  }, []);

  const handleGoToStepThree = useCallback(
    (formValue: UserInfo) => {
      handleSetUserInfo(formValue);
      goNextStep();
    },
    [handleSetUserInfo, goNextStep]
  );

  // --- Step 3 handlers ---

  const handleResetRolesToAssign = useCallback(() => {
    setState((s) => ({
      ...s,
      currentSelected: undefined,
      rolesToAssign: {
        ...s.rolesToAssign,
        rolesAssignment: { ...defaultRolesAssignment },
      },
    }));
  }, []);

  const handleCreateUser = useCallback(() => {
    const s = stateRef.current;

    const errors = validateCreatePayload({
      accountInfo: s.accountInfo,
      userInfo: s.userInfo,
      unitAddress: s.unitAddress,
    });

    if (errors.length > 0) {
      const msg = errors.map((e) => `${e.field}: ${e.message}`).join(', ');
      enqueueSnackbar({
        message: formatMessage({ id: 'account.error.validation' }) + ' ' + msg,
        variant: 'error',
      });
      return;
    }

    if (!s.unitAddress) return;

    const payload = buildCreateUserPayload({
      accountInfo: s.accountInfo,
      userInfo: s.userInfo,
      unitAddress: s.unitAddress,
      rolesToAssign: s.rolesToAssign,
      isCQQL: s.userInfo.organizationTypeId === 'CQQL',
    });

    createAccount({ data: payload });
  }, [createAccount, formatMessage, enqueueSnackbar]);

  const setCurrentSelected = useCallback((value: CurrentSelectedRole | undefined) => {
    setState((s) => ({ ...s, currentSelected: value }));
  }, []);

  const setRolesToAssign = useCallback(
    (value: RolesToAssign | ((prev: RolesToAssign) => RolesToAssign)) => {
      setState((s) => ({
        ...s,
        rolesToAssign: typeof value === 'function' ? value(s.rolesToAssign) : value,
      }));
    },
    []
  );

  return {
    state,
    isCQQL,
    isCreatePending,
    clients: clients || [],
    formatMessage,

    // Navigation
    goNextStep,
    goPreviousStep,
    setActiveStep,

    // Step 1
    handleSetAccountInfo,

    // Step 2
    handleSetUserInfo,
    handleBackToStepOne,
    handleSetUnitAddress,
    handleGoToStepThree,
    handleResetRolesToAssign,

    // Step 3
    handleCreateUser,
    setCurrentSelected,
    setRolesToAssign,
  };
}
